import React, { forwardRef, useEffect, useState } from "react";
import { Avatar, Skeleton } from "antd";
import "./Styles/PhotoSkeleton.scss";

// Photos that finished loading (or failed) this session - no skeleton for them again
const settledPhotos = new Set();
// After this long, stop waiting and show the photo as it is (the skeleton never sticks)
const PHOTO_TIMEOUT_MS = 10000;

const isSettled = (src) => {
  if (settledPhotos.has(src)) return true;
  const img = new Image();
  img.src = src;
  return img.complete && img.naturalWidth > 0; // already in the browser cache
};

// false while the photo is still downloading
function usePhotoReady(src) {
  const [ready, setReady] = useState(() => !src || isSettled(src));

  useEffect(() => {
    if (!src || isSettled(src)) {
      setReady(true);
      return undefined;
    }
    setReady(false);
    let active = true;
    const done = () => {
      settledPhotos.add(src);
      if (active) setReady(true);
    };
    const img = new Image();
    img.onload = done;
    img.onerror = done;
    img.src = src;
    const timer = setTimeout(() => active && setReady(true), PHOTO_TIMEOUT_MS);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [src]);

  return ready;
}

// The Care Pathways loader (antd Skeleton.Avatar), stretched to fill the avatar it sits in
const avatarSkeleton = <Skeleton.Avatar active shape="circle" className="photo-skeleton" />;
const hostClass = (className) => ["photo-skeleton-host", className].filter(Boolean).join(" ");

/**
 * The Care Pathways clinician loader for a photo, shown until it has downloaded. The photo element
 * (`children`: an antd <Avatar> or an <img>) stays in place the whole time, so its size and position
 * - from props or CSS - never change:
 *   - <Avatar>: the Skeleton fills the avatar, then the photo replaces it
 *   - <img>: the img shows the same shimmer in its own box until the photo is in
 */
export default function PhotoSkeleton({ src, children }) {
  const ready = usePhotoReady(src);
  if (ready || !React.isValidElement(children)) return children;

  if (children.type === Avatar) {
    return React.cloneElement(children, {
      className: hostClass(children.props.className),
      src: undefined,
      icon: avatarSkeleton,
      children: undefined,
    });
  }
  if (children.type === "img") {
    return React.cloneElement(children, {
      className: ["photo-skeleton-img", children.props.className].filter(Boolean).join(" "),
    });
  }
  return children;
}

/**
 * Drop-in for antd <Avatar src="url" /> with the same loader inside the avatar itself (same size
 * and place). Keeps Avatar's own fallback (icon / initial) for a photo that fails, and its ref,
 * so it still works as a Dropdown / Popover trigger.
 */
export const SkeletonAvatar = forwardRef(function SkeletonAvatar({ src, className, icon, children, ...avatarProps }, ref) {
  const ready = usePhotoReady(src);
  if (ready) {
    return (
      <Avatar ref={ref} src={src} className={className} icon={icon} {...avatarProps}>
        {children}
      </Avatar>
    );
  }
  return <Avatar ref={ref} className={hostClass(className)} icon={avatarSkeleton} {...avatarProps} />;
});
